import type { Ref } from 'react';
import type { OrganisationMembership } from '@ghostscreen/contracts';
import { organisationAvatarColor } from '../../lib/organisation/organisationAvatarColor';
import {
  describeOrganisationHealth,
  formatOrganisationSummary,
} from '../../lib/organisation/organisationHealth';
import { initialsFrom } from '../../lib/text/initials';

type OrganisationOptionProps = {
  organisation: OrganisationMembership;
  selected: boolean;
  /** Roving tab stop: exactly one option in the list is reachable with Tab. */
  tabbable: boolean;
  animationDelayMs: number;
  buttonRef: Ref<HTMLButtonElement>;
  onHighlight: () => void;
  onOpen: () => void;
};

export function OrganisationOption({
  organisation,
  selected,
  tabbable,
  animationDelayMs,
  buttonRef,
  onHighlight,
  onOpen,
}: OrganisationOptionProps) {
  const health = describeOrganisationHealth(organisation);

  return (
    <button
      ref={buttonRef}
      type="button"
      role="option"
      aria-selected={selected}
      tabIndex={tabbable ? 0 : -1}
      className="org-option"
      style={{ animationDelay: `${animationDelayMs}ms` }}
      onClick={onOpen}
      onMouseEnter={onHighlight}
      onFocus={onHighlight}
    >
      <span
        className="org-option__avatar"
        style={{ background: organisationAvatarColor(organisation.id) }}
        aria-hidden
      >
        <span>{initialsFrom(organisation.name)}</span>
      </span>
      <span className="org-option__text">
        <span className="org-option__name">{organisation.name}</span>
        <span className="org-option__meta">{formatOrganisationSummary(organisation)}</span>
      </span>
      <span className="org-option__status">
        <span className="org-option__dot" data-tone={health.tone} aria-hidden />
        {health.label}
      </span>
      <svg
        className="org-option__chevron"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="m9 6 6 6-6 6" />
      </svg>
    </button>
  );
}
